/** Allocates integer currency units proportionally while preserving totals and bounds. */
export function allocateBoundedBudgetUnits(
  originalUnits: number[],
  targetUnits: number,
  minimumUnits: number,
  maximumUnits: number,
): number[] | null {
  if (
    originalUnits.length === 0 ||
    targetUnits < originalUnits.length * minimumUnits ||
    targetUnits > originalUnits.length * maximumUnits
  ) {
    return null;
  }
  // A Campaign already at a bound cannot donate or absorb more. Scale the
  // original weights inside the bounds, then assign minor-unit remainders.
  let lower = 0;
  let upper = maximumUnits / Math.max(1, Math.min(...originalUnits));
  for (let iteration = 0; iteration < 80; iteration += 1) {
    const midpoint = (lower + upper) / 2;
    const total = originalUnits.reduce(
      (sum, units) => sum + Math.min(maximumUnits, Math.max(minimumUnits, midpoint * units)),
      0,
    );
    if (total <= targetUnits) lower = midpoint;
    else upper = midpoint;
  }
  const exact = originalUnits.map((units) =>
    Math.min(maximumUnits, Math.max(minimumUnits, lower * units)),
  );
  const allocatedUnits = exact.map(Math.floor);
  const remainder = targetUnits - allocatedUnits.reduce((total, units) => total + units, 0);
  const order = exact
    .map((units, index) => ({ index, fraction: units - Math.floor(units) }))
    .filter(({ index }) => (allocatedUnits[index] ?? 0) < maximumUnits)
    .sort((left, right) => right.fraction - left.fraction || left.index - right.index);
  if (remainder < 0 || remainder > order.length) return null;
  for (const { index } of order.slice(0, remainder))
    allocatedUnits[index] = (allocatedUnits[index] ?? 0) + 1;
  return allocatedUnits;
}
